import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./LowActivityMerchant.module.scss";
import { exportCsv, queryList, queryReferrer, querySales } from "./model";

const LowActivityMerchant = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "lowActivityMerchantGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [salesList, setSalesList] = useState([]);
  const [referrerList, setReferrerList] = useState([]);
  const [searchFieldInput, setSearchFieldInput] = useState({
    merchantId: "",
    merchantName: "",
    currency: "",
    sales: "",
    referrer: "",
  });
  const [focus, setFocus] = useState({});
  const [columnsArray, setColumnsArray] = useState([]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse === undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await getSales();
      await getReferrer();
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const response = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(t("general.message.initial.fetch.error"), "error");
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(false);
    setLoading(false);
  };

  const getSales = async () => {
    const response = await querySales({});
    const result = response?.data;
    if (result && result.data && Array.isArray(result.data)) {
      const newSalesList = [];
      result.data.forEach((sales) => {
        newSalesList.push({
          key: sales.id,
          title: sales.firstName,
          value: String(sales.id),
        });
      });
      setSalesList(newSalesList);
    } else {
      setSalesList([]);
    }
  };

  const getReferrer = async () => {
    const response = await queryReferrer({});
    const result = response?.data;
    if (result && result.data && Array.isArray(result.data)) {
      const newReferrerList = [];
      result.data.forEach((referrer) => {
        newReferrerList.push({
          key: referrer.id,
          title: referrer.company,
          value: String(referrer.id),
        });
      });
      setReferrerList(newReferrerList);
    } else {
      setReferrerList([]);
    }
  };

  const resetInputField = () => {
    setSearchFieldInput({
      merchantId: "",
      merchantName: "",
      currency: "",
      sales: "",
      referrer: "",
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const response = await queryList(payload);
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(true);
    setSearching(false);
  };

  const handleExport = async () => {
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const response = await exportCsv(payload);
    const result = response?.data;
    setExporting(false);
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.export.function.success.message"), "success");
      const url = result.data;
      window.location = url;
    }
    if (result && result.responseCode === "2200") {
      return snackbar(
        t("general.message.export.function.error.message.5"),
        "warning",
      );
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const columns = [
    {
      index: 0,
      key: "merchantId",
      title: t("page.analytics.low.activity.merchant.column.title.merchant.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "merchantName",
      title: t(
        "page.analytics.low.activity.merchant.column.title.merchant.name",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "currency",
      title: t(
        "page.analytics.low.activity.merchant.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "sum",
      title: t(
        "page.analytics.low.activity.merchant.column.title.total.transaction.volume",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "sales",
      title: t("page.analytics.low.activity.merchant.column.title.sales"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "referrer",
      title: t("page.analytics.low.activity.merchant.column.title.referrer"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "action",
      title: t(
        "page.analytics.referrer.transaction.volume.column.title.action",
      ),
      sorter: false,
      render: (data) => {
        const { merchantId } = data;
        const payload = {
          name: "Merchant Detail",
          path: "/analytics/transaction-activity/merchant-detail",
          state: {
            merchantId: merchantId,
          },
        };

        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS
              variant="text-only"
              onClick={() => redirectFn("redirectPage", payload)}
            >
              {t(
                "page.analytics.referrer.transaction.volume.column.title.action.1",
              )}
            </ButtonMAS>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t("page.analytics.low.activity.merchant.merchant.id.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t(
              "page.analytics.low.activity.merchant.merchant.name.label",
            )}
            value={searchFieldInput.merchantName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{ textFocus: focus.merchantName }}
          />
          <Selection
            label={t(
              "page.analytics.low.activity.merchant.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
          <Selection
            label={t("page.analytics.low.activity.merchant.sales.label")}
            selectionValue={searchFieldInput.sales}
            onchangeFn={(e) => textInputOnchange(e, "sales")}
            onfocusFn={() => textInputOnFocus("sales")}
            onblurFn={() => textInputOnBlur("sales")}
            options={salesList}
            sxParams={{ selectionFocus: focus.sales }}
          />
          <Selection
            label={t("page.analytics.low.activity.merchant.referrer.label")}
            selectionValue={searchFieldInput.referrer}
            onchangeFn={(e) => textInputOnchange(e, "referrer")}
            onfocusFn={() => textInputOnFocus("referrer")}
            onblurFn={() => textInputOnBlur("referrer")}
            options={referrerList}
            sxParams={{ selectionFocus: focus.referrer }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching || exporting}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>
              {t("page.analytics.low.activity.merchant.notice.content")}
            </span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching || exporting}
            exporting={exporting}
            exportBtn
            handleExport={handleExport}
            refreshBtn
            handleRefresh={handleSearch}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference: "analytics-low-activity-merchant",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(LowActivityMerchant);
